// src/pages/HorariosAmenidades.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { db } from '../firebase';
import { AMENITIES_CATALOG } from '../constants/amenidades';
import { registerMovement } from '../utils/registerMovement';
import './HorariosAmenidades.css';

const TIME_FORMAT = /^([01]\d|2[0-3]):([0-5]\d)$/;
const RANGE_FORMAT = /^([01]\d|2[0-3]):([0-5]\d)\s*-\s*([01]\d|2[0-3]):([0-5]\d)$/;
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 18;

const pad = (value) => String(value).padStart(2, '0');

const toRangeSlot = (value) => {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const rangeMatch = trimmed.match(RANGE_FORMAT);
    if (rangeMatch) {
        const [, startHour, startMinutes, endHour, endMinutes] = rangeMatch;
        const start = `${pad(startHour)}:${pad(startMinutes)}`;
        const end = `${pad(endHour)}:${pad(endMinutes)}`;
        if (start === end) {
            return null;
        }
        return `${start} - ${end}`;
    }

    const timeMatch = trimmed.match(TIME_FORMAT);
    if (!timeMatch) {
        return null;
    }

    const hour = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    const endHour = (hour + 1) % 24;
    const start = `${pad(hour)}:${pad(minutes)}`;
    const end = `${pad(endHour)}:${pad(minutes)}`;
    if (start === end) {
        return null;
    }
    return `${start} - ${end}`;
};

const sanitizeScheduleList = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }
    const unique = Array.from(new Set(
        value
            .map((slot) => toRangeSlot(slot))
            .filter(Boolean),
    ));
    unique.sort((a, b) => a.slice(0, 5).localeCompare(b.slice(0, 5)));
    return unique;
};

const sanitizeScheduleMap = (input) => {
    if (!input || typeof input !== 'object') {
        return {};
    }
    return Object.entries(input).reduce((acc, [amenidadId, value]) => {
        acc[amenidadId] = sanitizeScheduleList(value);
        return acc;
    }, {});
};

const generateDefaultHourlySlots = () => {
    const slots = [];
    for (let hour = DEFAULT_START_HOUR; hour < DEFAULT_END_HOUR; hour += 1) {
        const start = `${pad(hour)}:00`;
        const end = `${pad(hour + 1)}:00`;
        slots.push(`${start} - ${end}`);
    }
    return slots;
};

const getFraccionamientoId = (userData) => userData?.fraccionamientoId || userData?.fraccionamientoTd || null;

const HorariosAmenidades = () => {
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();

    const fraccionamientoId = getFraccionamientoId(userData);
    const [loading, setLoading] = useState(true);
    const [activeAmenityIds, setActiveAmenityIds] = useState([]);
    const [savedSchedules, setSavedSchedules] = useState({});
    const [formState, setFormState] = useState({});
    const [pendingTimes, setPendingTimes] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    const isDirtyRef = useRef(false);
    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    const savingRef = useRef(false);
    useEffect(() => {
        savingRef.current = saving;
    }, [saving]);

    const catalogById = useMemo(() => {
        const map = {};
        AMENITIES_CATALOG.forEach((amenidad) => {
            map[amenidad.id] = amenidad;
        });
        return map;
    }, []);

    useEffect(() => {
        if (!fraccionamientoId) {
            setActiveAmenityIds([]);
            setSavedSchedules({});
            setFormState({});
            setPendingTimes({});
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const unsubscribe = onSnapshot(fraccionamientoRef, (snapshot) => {
            const data = snapshot.data() || {};
            const activeIds = Array.isArray(data.amenidadesActivas)
                ? data.amenidadesActivas.filter((amenidadId) => typeof amenidadId === 'string' && amenidadId.trim().length > 0)
                : [];
            const cleanedSchedules = sanitizeScheduleMap(data.amenidadesHorarios);

            setActiveAmenityIds(activeIds);
            setSavedSchedules(cleanedSchedules);
            setLoading(false);

            setFormState((prev) => {
                const next = {};
                activeIds.forEach((amenidadId) => {
                    if (isDirtyRef.current && Array.isArray(prev[amenidadId])) {
                        next[amenidadId] = [...prev[amenidadId]];
                        return;
                    }

                    const stored = cleanedSchedules[amenidadId];
                    if (stored && stored.length > 0) {
                        next[amenidadId] = [...stored];
                        return;
                    }

                    next[amenidadId] = generateDefaultHourlySlots();
                });
                return next;
            });

            setPendingTimes((prev) => {
                const next = {};
                activeIds.forEach((amenidadId) => {
                    next[amenidadId] = prev[amenidadId] || '';
                });
                return next;
            });
        }, (error) => {
            console.error('HorariosAmenidades: error al escuchar fraccionamiento', error);
            setLoading(false);
            setActiveAmenityIds([]);
            if (!isDirtyRef.current) {
                setFormState({});
                setPendingTimes({});
            }
        });

        return () => {
            unsubscribe();
        };
    }, [fraccionamientoId, catalogById]);

    const activeAmenities = useMemo(
        () => activeAmenityIds
            .map((amenidadId) => catalogById[amenidadId] || { id: amenidadId, name: amenidadId, type: 'reservable' }),
        [activeAmenityIds, catalogById],
    );

    useEffect(() => {
        if (!fraccionamientoId || !activeAmenities.length) {
            return;
        }

        if (isDirtyRef.current || savingRef.current) {
            return;
        }

        const defaultsToPersist = activeAmenities.reduce((acc, amenidad) => {
            const current = savedSchedules[amenidad.id];
            if (!current || current.length === 0) {
                acc[amenidad.id] = generateDefaultHourlySlots();
            }
            return acc;
        }, {});

        const hasMissing = Object.keys(defaultsToPersist).length > 0;
        if (!hasMissing) {
            return;
        }

        const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const payload = Object.entries(defaultsToPersist).reduce((acc, [amenidadId, slots]) => {
            acc[`amenidadesHorarios.${amenidadId}`] = slots;
            return acc;
        }, {});

        const persistDefaults = async () => {
            try {
                await setDoc(fraccionamientoRef, payload, { merge: true });
            } catch (error) {
                console.error('HorariosAmenidades: error al garantizar horarios predeterminados', error);
            }
        };

        persistDefaults();
    }, [activeAmenities, fraccionamientoId, savedSchedules]);

    const handleTimeChange = useCallback((amenidadId, value) => {
        setPendingTimes((prev) => ({
            ...prev,
            [amenidadId]: value,
        }));
    }, []);

    const handleAddSlot = useCallback((amenidadId) => {
        const value = (pendingTimes[amenidadId] || '').trim();
        if (!value) {
            return;
        }

        const range = toRangeSlot(value);
        if (!range) {
            showNotification('Ingresa un horario válido en formato HH:MM (ej. 09:00) para crear un bloque de una hora.', 'warning');
            return;
        }

        const current = Array.isArray(formState[amenidadId]) ? formState[amenidadId] : [];
        if (current.includes(range)) {
            showNotification('Ese horario ya está configurado para la amenidad.', 'info');
            return;
        }

        const updated = [...current, range].sort((a, b) => a.slice(0, 5).localeCompare(b.slice(0, 5)));
        setFormState((prev) => ({
            ...prev,
            [amenidadId]: updated,
        }));
        setPendingTimes((prev) => ({
            ...prev,
            [amenidadId]: '',
        }));
        setIsDirty(true);
    }, [formState, pendingTimes, showNotification]);

    const handleRemoveSlot = useCallback((amenidadId, slot) => {
        setFormState((prev) => {
            const current = Array.isArray(prev[amenidadId]) ? prev[amenidadId] : [];
            const updated = current.filter((item) => item !== slot);
            if (updated.length === current.length) {
                return prev;
            }
            setIsDirty(true);
            return {
                ...prev,
                [amenidadId]: updated,
            };
        });
    }, []);

    const handleRestoreDefaults = useCallback((amenidadId) => {
        setFormState((prev) => ({
            ...prev,
            [amenidadId]: generateDefaultHourlySlots(),
        }));
        setIsDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (!fraccionamientoId || !activeAmenities.length) {
            return;
        }

        const payload = { ...savedSchedules };
        const changeSummary = [];
        activeAmenities.forEach((amenidad) => {
            const sanitized = sanitizeScheduleList(formState[amenidad.id]);
            const nextSlots = sanitized.length > 0 ? sanitized : generateDefaultHourlySlots();
            payload[amenidad.id] = nextSlots;

            const previousSlots = Array.isArray(savedSchedules[amenidad.id]) ? savedSchedules[amenidad.id] : [];
            const added = nextSlots.filter((slot) => !previousSlots.includes(slot));
            const removed = previousSlots.filter((slot) => !nextSlots.includes(slot));
            if (added.length > 0 || removed.length > 0) {
                changeSummary.push({
                    amenidadId: amenidad.id,
                    amenidadNombre: amenidad.nombre || amenidad.label || amenidad.id,
                    agregados: added,
                    eliminados: removed,
                });
            }
        });

        setSaving(true);
        try {
            const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
            await setDoc(fraccionamientoRef, { amenidadesHorarios: payload }, { merge: true });
            setIsDirty(false);
            showNotification('Horarios actualizados correctamente.', 'success');

            if (changeSummary.length > 0) {
                const actorNombre = [
                    userData?.nombreCompleto,
                    userData?.nombre,
                    currentUser?.displayName,
                    currentUser?.email,
                ].find((value) => typeof value === 'string' && value.trim().length > 0) || currentUser?.uid || 'Usuario';

                try {
                    await registerMovement({
                        fraccionamientoId,
                        titulo: 'Horarios de amenidades actualizados',
                        descripcion: `${actorNombre} modificó los horarios de ${changeSummary.length} amenidad(es).`,
                        categoria: 'Amenidades',
                        tipoMovimiento: 'horarios_amenidades_actualizados',
                        origen: 'amenidades',
                        metadata: {
                            cambios: changeSummary,
                            actorUid: currentUser?.uid || null,
                            actorEmail: currentUser?.email || null,
                        },
                    });
                } catch (logError) {
                    console.error('HorariosAmenidades: no se pudo registrar la actualización de horarios', logError);
                }
            }
        } catch (error) {
            console.error('HorariosAmenidades: error al guardar horarios', error);
            showNotification('No se pudieron guardar los horarios. Intenta nuevamente.', 'error');
        } finally {
            setSaving(false);
        }
    }, [activeAmenities, formState, fraccionamientoId, savedSchedules, showNotification, currentUser, userData]);

    const hasAmenities = activeAmenities.length > 0;
    const isSaveDisabled = !isDirty || saving || !hasAmenities;

    return (
        <div className="horarios-page">
            <header className="horarios-header">
                <div>
                    <h2>Horarios de amenidades</h2>
                    <p>Configura los horarios disponibles para cada amenidad activa.</p>
                </div>
                <Link to="/configuraciones" className="horarios-back-link">&lt; Volver a Configuraciones</Link>
            </header>

            {!fraccionamientoId && (
                <div className="horarios-alert">
                    No se encontró un fraccionamiento asociado a tu usuario. Contacta a soporte para continuar.
                </div>
            )}

            {loading && fraccionamientoId && (
                <div className="horarios-loading">Cargando configuración de amenidades…</div>
            )}

            {!loading && fraccionamientoId && activeAmenities.length === 0 && (
                <div className="horarios-empty">
                    No hay amenidades activas en tu fraccionamiento. Pide al manager que active las amenidades necesarias.
                </div>
            )}

            <div className="horarios-grid">
                {activeAmenities.map((amenidad) => {
                    const iconElement = amenidad.Icon ? <amenidad.Icon /> : null;
                    const slots = Array.isArray(formState[amenidad.id]) ? formState[amenidad.id] : [];
                    const pendingValue = pendingTimes[amenidad.id] || '';

                    const supportsReservations = amenidad.type === 'reservable';
                    const description = supportsReservations
                        ? 'Define los horarios disponibles para reservas.'
                        : 'Define los horarios de disponibilidad visibles para los residentes.';
                    const canAddSlot = Boolean(toRangeSlot(pendingValue));

                    return (
                        <section key={amenidad.id} className="horarios-card">
                            <header className="horarios-card-header">
                                <span className="horarios-card-icon">{iconElement}</span>
                                <div>
                                    <h3>{amenidad.name || amenidad.id}</h3>
                                    <p>{description}</p>
                                    {!supportsReservations && (
                                        <div className="horarios-card-note">
                                            Estas amenidades no generan reservas automáticas, pero los horarios se muestran para referencia y gestión interna.
                                        </div>
                                    )}
                                </div>
                            </header>

                            <div className="horarios-chip-list" aria-label={`Horarios configurados para ${amenidad.name}`}>
                                {slots.length === 0 && (
                                    <span className="horarios-chip muted">Sin horarios definidos</span>
                                )}
                                {slots.map((slot) => (
                                    <button
                                        key={slot}
                                        type="button"
                                        className="horarios-chip"
                                        onClick={() => handleRemoveSlot(amenidad.id, slot)}
                                        title="Quitar horario"
                                    >
                                        {slot}
                                        <span aria-hidden="true">×</span>
                                    </button>
                                ))}
                            </div>

                            <div className="horarios-actions">
                                <label htmlFor={`nuevo-horario-${amenidad.id}`} className="sr-only">
                                    Nuevo horario para {amenidad.name}
                                </label>
                                <input
                                    id={`nuevo-horario-${amenidad.id}`}
                                    type="time"
                                    value={pendingValue}
                                    onChange={(event) => handleTimeChange(amenidad.id, event.target.value)}
                                    disabled={saving}
                                />
                                <button
                                    type="button"
                                    onClick={() => handleAddSlot(amenidad.id)}
                                    disabled={saving || !canAddSlot}
                                >
                                    Agregar horario
                                </button>
                                <button
                                    type="button"
                                    className="link"
                                    onClick={() => handleRestoreDefaults(amenidad.id)}
                                    disabled={saving}
                                >
                                    Restaurar predeterminados
                                </button>
                            </div>
                        </section>
                    );
                })}
            </div>

            {fraccionamientoId && hasAmenities && (
                <div className="horarios-save-bar">
                    <button type="button" onClick={handleSave} disabled={isSaveDisabled}>
                        {saving ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                    {isDirty && !saving && <span className="horarios-unsaved">Tienes cambios sin guardar.</span>}
                </div>
            )}
        </div>
    );
};

export default HorariosAmenidades;
